import React, { useState } from 'react';
import moment from 'moment';
import { MapPin } from 'lucide-react';
import { fractionAlong, axisTicksFor, type TimeRange } from './timeAxis';

export type TimelineSpan = {
    /** Identifies the span, and completes its test id. */
    id: string;
    /** What the bar is called, on the bar and in its accessible name. */
    label: string;
    from: Date;
    /** `null` while the span is still open, which is what draws it to the edge of the window. */
    to: Date | null;
    /** Began before the window opened, and was carried in so the window is not blank. */
    carriedIn?: boolean;
    /** Ended at an instant outside the window, so `to` is where the drawing stops, not the end. */
    endsBeyondWindow?: boolean;
};

/**
 * What the bars are called in prose.
 *
 * The shapes are the same whatever is being drawn; the words are the caller's domain. A
 * catalogue says a version was *in force*; a rota would say someone was *on call*.
 */
export type SpanWording = {
    /** Joins a span to its dates: "<label>, in force <from> to <to>". */
    held: string;
    /** Where the end would go, for a span that has not ended. */
    ongoing: string;
    /** Where the end would go, for one that ended outside the window. */
    beyondWindow: string;
    /** Appended to a span carried in from before the window. */
    carriedIn: string;
};

const PLAIN_WORDING: SpanWording = {
    held: 'held',
    ongoing: 'now and still current',
    beyondWindow: 'an instant beyond this window',
    carriedIn: 'began before this window',
};

type SpanTimelineProps = {
    spans: TimelineSpan[];
    /** The whole axis. */
    bounds: TimeRange;
    /** The span whose detail is on screen. */
    selectedId?: string;
    /** The instant the playhead points at. */
    atInstant: Date;
    onAtChange: (at: Date) => void;
    onSelect: (id: string) => void;
    /** Sits in the card's top corner, beside the timeline it changes. */
    controls?: React.ReactNode;
    wording?: Partial<SpanWording>;
    /** Completes `<prefix>-<id>` on each bar, so two timelines on a page do not collide. */
    testIdPrefix?: string;
    /** How fine the playhead's steps are. Days by default. */
    stepMs?: number;
};

const formatDay = (at: Date) => moment.utc(at).format('D MMM YYYY');

/**
 * How wide the invisible grip on the playhead is.
 *
 * Also the width the input is widened by, so the handle's travel matches the bars. Kept in step
 * with the `w-5` on the thumb by hand: Tailwind needs those class names written out, so they
 * cannot be interpolated from here.
 */
export const GRIP_WIDTH_PX = 20;

/** Clears the pin, so the label sits beside it rather than under its edge. */
export const LABEL_GAP_PX = 12;

/**
 * How near the start the marker has to be before its label changes sides.
 *
 * The label reads best to the left, over track already passed. Near the start there is no track
 * to its left, so it flips rather than hang off the frame. A tenth is about what the pill takes
 * up on a timeline of any usual width — a rough figure, and it only decides which side of a
 * marker the same text appears on.
 */
const LABEL_FLIP_FRACTION = 0.12;

/** Small enough not to distort a short span, wide enough to stay clickable. */
const MIN_BAR_PERCENT = 1.5;

/** A day. Finer than the instants most callers hold, and a sane default to drag through. */
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Spans on a time axis, with a playhead over them.
 *
 * Bars rather than points, because each thing drawn holds from one instant until another. One
 * with no end runs to the edge of the window, which is what makes "still current" visible at a
 * glance.
 *
 * Bars are buttons, so they focus, press and announce themselves without anything
 * re-implemented. Identity never rests on colour: the selected bar is `aria-pressed`, and every
 * bar names itself and the span it held — which is why no table need repeat them beneath.
 *
 * Nothing here knows what a span *is*. The caller turns its own records into `TimelineSpan`s and
 * says, in `wording`, what holding one is called.
 */
const SpanTimeline = ({
    spans,
    bounds,
    selectedId,
    atInstant,
    onAtChange,
    onSelect,
    controls,
    wording,
    testIdPrefix = 'timeline-span',
    stepMs = DAY_IN_MS,
}: SpanTimelineProps) => {
    const says = { ...PLAIN_WORDING, ...wording };
    const [hovered, setHovered] = useState<TimelineSpan | null>(null);
    const ticks = axisTicksFor(bounds);

    // One number for everything that tracks the instant: the label, the pin and its line.
    const playheadPercent = fractionAlong(atInstant, bounds) * 100;
    const isLabelBeforeMarker = playheadPercent / 100 >= LABEL_FLIP_FRACTION;

    const geometryOf = (segment: TimelineSpan) => {
        const left = fractionAlong(segment.from, bounds) * 100;
        // No end means still in force, which is what draws it to the edge.
        const right = segment.to ? fractionAlong(segment.to, bounds) * 100 : 100;
        return { left, width: Math.max(right - left, MIN_BAR_PERCENT) };
    };

    return (
        <div className='relative rounded-xl border border-border bg-white px-4 pb-3 pt-3'>
            {controls && <div className='mb-2 flex justify-start'>{controls}</div>}
            {hovered && (
                <div
                    className='absolute top-1 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs text-white shadow-sm'
                    style={{ left: `${6 + (geometryOf(hovered).left + geometryOf(hovered).width / 2) * 0.88}%` }}
                    data-testid='timeline-tooltip'
                    role='status'
                >
                    <span className='font-semibold'>{hovered.label}</span>
                    <span className='ml-2 opacity-80'>
                        {formatDay(hovered.from)} →{' '}
                        {hovered.endsBeyondWindow ? 'beyond this window' : hovered.to ? formatDay(hovered.to) : 'Present'}
                    </span>
                </div>
            )}

            {/* One track carrying every version, because at most one is ever in force. */}
            {/* The marker sits in its own lane rather than over the bars: laid on top it
                    would swallow every click meant for a version. Clicking the lane moves it
                    there, and the line runs down through the bars so the instant can be read
                    against them. */}
            {/* A native range input: it is the platform's own slider, so dragging, the arrow
                keys, Home/End, touch and the whole `role="slider"` contract come with it and
                nothing here re-implements them. What is visible is drawn over it — a pseudo
                element cannot hold an icon — leaving the input as an invisible grip.

                It keeps its own lane rather than lying over the bars: laid on top it would
                swallow every click meant for a version. */}
            <div className='relative z-20 h-5' data-testid='timeline-at-control'>
                <input
                    type='range'
                    min={bounds.from.getTime()}
                    max={bounds.to.getTime()}
                    step={stepMs}
                    value={atInstant.getTime()}
                    aria-label={`Map showing the catalogue as it stood on ${formatDay(atInstant)}`}
                    onChange={event => onAtChange(new Date(Number(event.target.value)))}
                    // A range input insets its handle by half its width at each end, so the
                    // handle cannot overhang the track — which would put every position out
                    // against bars measured in plain percentages. Widened by a grip and pulled
                    // back half of one, the handle travels exactly the bars' nought to a
                    // hundred, and one coordinate system serves the whole timeline.
                    style={{ width: `calc(100% + ${GRIP_WIDTH_PX}px)`, left: `-${GRIP_WIDTH_PX / 2}px` }}
                    className={[
                        'absolute inset-y-0 cursor-ew-resize appearance-none bg-transparent',
                        'focus-visible:outline-none',
                        // Invisible: the marker drawn over it is what is seen. Sized once per
                        // engine, there being no shared pseudo-element — and `w-5` here is
                        // GRIP_WIDTH_PX, which the compensation above depends on.
                        '[&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none',
                        '[&::-webkit-slider-thumb]:cursor-ew-resize [&::-webkit-slider-thumb]:bg-transparent',
                        '[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:border-0',
                        '[&::-moz-range-thumb]:bg-transparent',
                        '[&::-webkit-slider-runnable-track]:bg-transparent [&::-moz-range-track]:bg-transparent',
                    ].join(' ')}
                />

                {/* The marker itself. `pointer-events-none` throughout: the input beneath is
                    what is dragged, and the line lying over the bars must not swallow clicks
                    meant for a version. The pin's tip is the instant, so the line starts there. */}
                {/* The date the marker points at, riding above it — the pin alone is a
                    decoration until a reader discovers it moves, and the instant is written
                    down nowhere else on the timeline.

                    Anchored by how far along it is rather than centred: at the start its left
                    edge sits on the marker, at the end its right edge does, and in between it
                    is centred. So it never hangs off the track, and nothing has to be measured
                    to keep it in. */}
                <span
                    className='pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium text-white shadow-sm'
                    style={{
                        left: `${playheadPercent}%`,
                        transform: isLabelBeforeMarker ? `translateX(calc(-100% - ${LABEL_GAP_PX}px))` : `translateX(${LABEL_GAP_PX}px)`,
                    }}
                    data-testid='timeline-at-label'
                >
                    {formatDay(atInstant)}
                </span>

                <span
                    className='pointer-events-none absolute bottom-0 -translate-x-1/2 text-foreground'
                    style={{ left: `${playheadPercent}%` }}
                    data-testid='timeline-at-handle'
                >
                    <MapPin className='h-5 w-5 fill-foreground stroke-white' aria-hidden='true' />
                    <span
                        // `h-10`: the 4px gap below this lane plus the 36px of bars. The halo
                        // lets it read against a filled bar and the empty rail alike.
                        className='absolute left-1/2 top-full h-10 w-0.5 -translate-x-1/2 bg-foreground shadow-[0_0_0_1px_rgba(255,255,255,0.9)]'
                        data-testid='timeline-at-marker'
                        aria-hidden='true'
                    />
                </span>
            </div>

            <div className='relative mt-1 h-9 overflow-hidden rounded-md bg-secondary' data-testid='timeline-track'>
                {ticks.map(tick => (
                    <div
                        key={`grid-${tick.at.toISOString()}`}
                        className='absolute top-0 h-full border-l border-border'
                        style={{ left: `${fractionAlong(tick.at, bounds) * 100}%` }}
                        aria-hidden='true'
                    />
                ))}

                {spans.map((segment, index) => {
                    const { left, width } = geometryOf(segment);
                    const isSelected = segment.id === selectedId;
                    const isOngoing = segment.to === null;

                    return (
                        <button
                            key={segment.id}
                            type='button'
                            data-testid={`${testIdPrefix}-${segment.id}`}
                            aria-pressed={isSelected}
                            aria-label={`${segment.label}, ${says.held} ${formatDay(segment.from)} to ${
                                isOngoing ? says.ongoing : segment.endsBeyondWindow ? says.beyondWindow : formatDay(segment.to as Date)
                            }${segment.carriedIn ? `, ${says.carriedIn}` : ''}`}
                            style={{
                                left: `${left}%`,
                                width: `${width}%`,
                                // Alternating weight separates neighbours without a second hue.
                                opacity: isSelected ? 1 : index % 2 === 0 ? 0.9 : 0.7,
                            }}
                            onMouseEnter={() => setHovered(segment)}
                            onMouseLeave={() => setHovered(null)}
                            onFocus={() => setHovered(segment)}
                            onBlur={() => setHovered(null)}
                            onClick={() => onSelect(segment.id)}
                            className={[
                                'group absolute top-0 flex h-full items-center overflow-hidden px-2 transition-colors',
                                // A 2px surface gap, so adjacent bars read as two and not one.
                                index > 0 ? 'border-l-2 border-white' : '',
                                // Selection is a stronger fill, not an outline: a ring in the
                                // text colour reads as a border drawn round a block of teal.
                                isSelected ? 'z-10 bg-primary' : 'bg-primary/55 hover:bg-primary/75',
                                'focus:outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white',
                            ].join(' ')}
                        >
                            {/* Named on the bar where it fits, so a reader need not hover to
                                    know which version they are looking at. */}
                            <span className='truncate text-[11px] font-medium text-white'>{segment.label}</span>
                            {segment.carriedIn && (
                                <span className='sr-only' data-testid={`${testIdPrefix}-carried-in-${segment.id}`}>
                                    carried in from before this window
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className='relative mt-1 h-4 overflow-hidden' data-testid='timeline-axis' aria-hidden='true'>
                {ticks.map(tick => (
                    <span
                        key={tick.at.toISOString()}
                        className='absolute text-[10px] text-muted-foreground'
                        style={{ left: `${fractionAlong(tick.at, bounds) * 100}%` }}
                    >
                        {tick.label}
                    </span>
                ))}
            </div>

            <div className='mt-4 flex flex-col gap-3'></div>

            {spans.length === 0 && (
                <p className='pb-2 pt-3 text-center text-sm text-muted-foreground' data-testid='timeline-empty'>
                    No version was in force in this window. Widen it to reach earlier ones.
                </p>
            )}
        </div>
    );
};

export default SpanTimeline;
